/// <reference types="@types/google.maps" />
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AutoCompleteResult, isNullOrUndefined, ServiceException } from '@uzurv/core';
import { IMapMarker, UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { Location, RideRequestLocation } from '../../models';
import { GOOGLE_MAPS_STYLES } from './maps.styles';

declare let MarkerClusterer: any;

export interface IGeoCoords {
  lng: number;
  lat: number;
  bearing?: number;
}

export interface IMapRoute {
  polyline: string;
  startNode?: IGeoCoords;
  endNode?: IGeoCoords;
  distance?: number;
  duration?: number;
  startAddress?: string;
  endAddress?: string;
  type?: string;
  color?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MapsService extends UzurvRestService {
  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns autocomplete results from the Google Maps Places library
   *
   * @param query
   */
  autocomplete(query?: string): Subject<Array<AutoCompleteResult>> {
    const autocompleteService = new google.maps.places.AutocompleteService();

    const obs = new Subject<Array<AutoCompleteResult>>();

    autocompleteService.getQueryPredictions({ input: query }, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK && status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        obs.error(status);
        obs.complete();
      }
      obs.next(this.convertGoogleMapsAutoSuggestResultsToAutoCompleteResults(predictions));
      obs.complete();
    });

    return obs;
  }

  /**
   * Performs a geocode using the supplied search string from the Google Maps Geocoder Library
   *
   * @param searchString
   */
  geocodeLatLngBySearchString(searchString: string): Subject<Location> {
    const obs = new Subject<Location>();

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: searchString }, (geocodedLocation: google.maps.GeocoderResult[]) => {
      if (isNullOrUndefined(geocodedLocation) || geocodedLocation.length === 0) {
        obs.error(new ServiceException('No results searchString ' + searchString));
        return;
      }

      obs.next(this.convertGoogleMapsGeocodeResultToUzurvLocation(geocodedLocation[0]));
    });

    return obs;
  }

  /**
   * Performs a reverse geocode using the supplied AutoCompleteResult from the Google Maps Geocoder Library
   *
   * @param autoCompleteResult
   */
  geocodeLocationByAutoCompleteResult(autoCompleteResult: AutoCompleteResult): Subject<Location> {
    const obs = new Subject<Location>();

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ placeId: autoCompleteResult.locationId }, (geocodedLocation: Array<google.maps.GeocoderResult>) => {
      if (isNullOrUndefined(geocodedLocation) || geocodedLocation.length === 0) {
        obs.error(new ServiceException('No results for location with place id ' + autoCompleteResult.locationId));
        return;
      }
      obs.next(this.convertGoogleMapsGeocodeResultToUzurvLocation(geocodedLocation[0], autoCompleteResult));
    });

    return obs;
  }

  /**
   * Performs a reverse geocode using the supplied Place ID from the Google Maps Geocoder Library
   *
   * @param placeId
   */
  geocodeLocationByPlaceId(placeId: string): Subject<Location> {
    const obs = new Subject<Location>();

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ placeId: placeId }, (geocodedLocation: Array<google.maps.GeocoderResult>) => {
      if (isNullOrUndefined(geocodedLocation) || geocodedLocation.length === 0) {
        obs.error(new ServiceException('No results for location with place id ' + placeId));
        return;
      }
      obs.next(this.convertGoogleMapsGeocodeResultToUzurvLocation(geocodedLocation[0]));
    });

    return obs;
  }

  /**
   * Performs a reverse geocode using the supplied LatLng from the Google Maps Geocoder Library
   *
   * @param lat
   * @param lng
   */
  geocodeLocationByLatLng(lat: number, lng: number): Subject<Location> {
    const obs = new Subject<Location>();

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ location: new google.maps.LatLng(lat, lng) }, (geocodedLocation: Array<google.maps.GeocoderResult>) => {
      obs.next(this.convertGoogleMapsGeocodeResultToUzurvLocation(geocodedLocation[0]));
    });

    return obs;
  }

  /**
   * When a location lacks latittude and longitude, a new geocode is performed to supply the requred information
   *
   * @deprecated
   * @param location
   */
  updateLatLonForRideRequestLocationLocation(rideRequestLocation: RideRequestLocation): Observable<RideRequestLocation> {
    const obs = new ReplaySubject<RideRequestLocation>(1);

    this.autocomplete(rideRequestLocation.display).subscribe(autoCompleteResults => {
      this.geocodeLocationByPlaceId(autoCompleteResults[0].locationId).subscribe(
        newLocation => {
          rideRequestLocation.latitude = newLocation.latitude;
          rideRequestLocation.longitude = newLocation.longitude;
          obs.next(rideRequestLocation);
          obs.complete();
        },
        (/* error */) => {
          obs.error(new ServiceException('Could not find location data.  Please try entering this location manually'));
          obs.complete();
        }
      );
    });

    return obs;
  }

  /**
   * Generates a static map image using the supplied parameters
   *
   * @param width
   * @param height
   * @param routePolyline
   * @param markers
   */
  getMapImageUrl(width: number, height: number, routePolyline: string = null, markers: Array<IMapMarker> = []): string {
    let url = 'https://maps.googleapis.com/maps/api/staticmap?key=' + this.uzurvRestConfiguration.googleMapsApiKey;
    url += '&size=' + width + 'x' + height;

    if (!isNullOrUndefined(routePolyline)) {
      url += '&path=color:0x0000ff|weight:5|enc:' + routePolyline;
    }

    markers.forEach(marker => {
      // url += '&markers=anchor:top|icon:' + marker.iconUrl;
      url += '&markers=size:mid|color:' + marker.color;
      marker.locations.forEach(location => {
        url += '|' + location;
      });
    });

    return url;
  }

  /**
   * Generates a static map image using the supplied parameters for a mobile app.
   *
   * @param width
   * @param height
   * @param routePolyline
   * @param markers
   * @param polylineColor
   */
  getMapImageUrlMobile(
    width: number,
    height: number,
    routePolyline: string = null,
    markers: Array<IMapMarker> = [],
    polylineColor: string
  ): string {
    let url = 'https://maps.googleapis.com/maps/api/staticmap?key=' + this.uzurvRestConfiguration.googleMapsApiKey;
    url += '&size=' + width + 'x' + height;

    if (!isNullOrUndefined(routePolyline)) {
      url += '&path=color:' + polylineColor + '|weight:5|enc:' + routePolyline;
    }

    const format: string =
      // eslint-disable-next-line max-len
      '&format=png&maptype=roadmap&style=element:geometry%7Ccolor:0xf5f5f5&style=element:labels.icon%7Cvisibility:off&style=element:labels.text.fill%7Ccolor:0x616161&style=element:labels.text.stroke%7Ccolor:0xf5f5f5&style=feature:administrative.land_parcel%7Celement:labels.text.fill%7Ccolor:0xbdbdbd&style=feature:poi%7Celement:geometry%7Ccolor:0xeeeeee&style=feature:poi%7Celement:labels.text.fill%7Ccolor:0x757575&style=feature:poi.park%7Celement:geometry%7Ccolor:0xe5e5e5&style=feature:poi.park%7Celement:labels.text.fill%7Ccolor:0x9e9e9e&style=feature:road%7Celement:geometry%7Ccolor:0xffffff&style=feature:road.arterial%7Celement:labels.text.fill%7Ccolor:0x757575&style=feature:road.highway%7Celement:geometry%7Ccolor:0xdadada&style=feature:road.highway%7Celement:labels.text.fill%7Ccolor:0x616161&style=feature:road.local%7Celement:labels.text.fill%7Ccolor:0x9e9e9e&style=feature:transit.line%7Celement:geometry%7Ccolor:0xe5e5e5&style=feature:transit.station%7Celement:geometry%7Ccolor:0xeeeeee&style=feature:water%7Celement:geometry%7Ccolor:0xc9c9c9&style=feature:water%7Celement:labels.text.fill%7Ccolor:0x9e9e9e&size=480x360';

    url += format;

    markers.forEach(marker => {
      if (!isNullOrUndefined(marker.iconUrl)) {
        url += '&markers=|icon:' + marker.iconUrl;
        if (marker.anchor) {
          url += '|anchor:' + marker.anchor;
        }
      } else {
        url += '|size:mid|color:' + marker.color;
      }

      marker.locations.forEach(location => {
        url += '|' + location;
      });
    });

    return url;
  }

  /**
   * Initiates  a new map object centered at the coordinates and zoom level specified
   *
   * @param container the name of the div, by id, where the map will be injected
   * @param center a GPS coordinate as IGeoCoords
   * @param zoom Zoom level of the map (~10 is normal)
   */
  buildMap(
    container: HTMLElement,
    center: IGeoCoords,
    zoom: number,
    enableGestures: boolean = true,
    keyboardShortcuts: boolean = true
  ): google.maps.Map {
    const mapProp: google.maps.MapOptions = {
      center: isNullOrUndefined(center) ? null : new google.maps.LatLng(center.lat, center.lng),
      zoom: zoom,
      gestureHandling: enableGestures ? 'cooperative' : 'none',
      clickableIcons: Boolean(enableGestures),
      keyboardShortcuts: Boolean(keyboardShortcuts),
      disableDefaultUI: true,
      styles: GOOGLE_MAPS_STYLES
    };

    return new google.maps.Map(container, mapProp);
  }

  /**
   * Constructs  a new polygon using the given options
   *
   * @param opts google maps polygon options
   */
  constructPolygon(opts: google.maps.PolygonOptions): google.maps.Polygon {
    return new google.maps.Polygon(opts);
  }

  /**
   * Constructs  a new circle using the given options
   *
   * @param opts google maps cirlcle options
   */
  constructCircle(opts: google.maps.CircleOptions): google.maps.Circle {
    return new google.maps.Circle(opts);
  }

  /**
   * Fits a Google Maps view to contain the points on a given polyline by iterating
   * through and keeping the polyline points inside the map
   *
   * @param map the Google Maps Map to fit
   * @param polyline the polyline of the route being fit
   * @param car optionally fits the view to include the driver's location as well
   */
  fitMap(map: google.maps.Map, polyline: google.maps.Polyline, car?: google.maps.Marker): void {
    const polylinePoints = polyline.getPath().getArray();
    const bounds = new google.maps.LatLngBounds();

    bounds.extend(polylinePoints[0]);
    bounds.extend(polylinePoints[polylinePoints.length - 1]);

    map.fitBounds(bounds);
  }

  /**
   * Returns a car marker at the location specified, attached in the map object specified
   *
   * @param location a GPS coordinate as IGeoCoords
   * @param map the Google Maps Map the marker should be attached to
   */
  buildMarker(
    location: IGeoCoords,
    map: google.maps.Map,
    bearing: number,
    infoWindow?: google.maps.InfoWindow,
    color?: string
  ): google.maps.Marker {
    const marker = new google.maps.Marker({
      position: location,
      map: map,
      icon: this.buildImage(bearing, color)
    });

    if (!isNullOrUndefined(infoWindow)) {
      marker.addListener('mouseover', () => {
        infoWindow.open(map, marker);
      });

      marker.addListener('mouseout', () => {
        infoWindow.close();
      });
    }

    marker.addListener('click', () => {
      map.setCenter(marker.getPosition());
      map.setZoom(15);
    });

    return marker;
  }

  /**
   * Returns a route node marker at the location specified, attached in the map object specified
   *
   * @param location a GPS coordinate as IGeoCoords
   * @param map the Google Maps Map object the marker should be attached to
   */
  buildNodeMarker(options: {
    location: IGeoCoords;
    map: google.maps.Map;
    color: string;
    info?: string;
    zIndex?: number;
  }): google.maps.Marker {
    let iconUrl: string;

    switch (options.color) {
      case 'green':
        iconUrl = 'assets/map-marker-green.png';
        break;
      case 'red':
        iconUrl = 'assets/map-marker-red.png';
        break;
      case 'selected':
        iconUrl = 'assets/selected.png';
        break;
      case 'unselected':
        iconUrl = 'assets/unselected.png';
        break;
      case 'selected_hovered':
        iconUrl = 'assets/selected_hovered.png';
        break;
      case 'unselected_hovered':
        iconUrl = 'assets/unselected_hovered.png';
        break;
      case 'new':
        iconUrl = 'assets/new.png';
        break;
      default:
        iconUrl = options.color;
        break;
    }

    const marker = new google.maps.Marker({
      position: options.location,
      map: options.map,
      icon: iconUrl,
      zIndex: options.zIndex
    });

    if (!isNullOrUndefined(options.info)) {
      const infoWindow = new google.maps.InfoWindow({
        content: options.info,
        disableAutoPan: true
      });

      marker.addListener('mouseover', () => {
        infoWindow.open(options.map, marker);
      });

      marker.addListener('mouseout', () => {
        infoWindow.close();
      });

      // Setting this as marker data so it can be retrieved and closed
      marker.set('infoWindow', infoWindow);
    }

    marker.addListener('click', () => {
      options.map.setCenter(marker.getPosition());
      options.map.setZoom(15);
    });

    return marker;
  }

  /**
   * Adds and returns a polyline attached to a map that visualizes
   * the driving directions. Markers are also added to the start and
   * end nodes
   *
   * @param route A Uzurv route object as an IMapRoute object
   * @param map the Google Maps Map the route should be added to
   */
  buildRoute(route: IMapRoute, map: google.maps.Map, strokeColor = '#4064BD80', info?: string): google.maps.Polyline {
    const decodedPath = google.maps.geometry.encoding.decodePath(route.polyline);
    const middle = decodedPath[Math.floor(decodedPath.length / 2)];
    const routePolyline = new google.maps.Polyline({
      path: decodedPath,
      geodesic: true,
      strokeColor: strokeColor,
      strokeOpacity: 1.0,
      strokeWeight: 3
    });

    routePolyline.setMap(map);

    if (!isNullOrUndefined(info)) {
      const infoWindow = new google.maps.InfoWindow({
        content: info,
        position: middle
      });

      // Setting this as polyline data so it can be retrieved and closed
      routePolyline.set('infoWindow', infoWindow);

      infoWindow.open(map);
    }

    return routePolyline;
  }

  /**
   * Given a set of map markers, turns on the clustering visualization to de-clutter the map
   *
   * @param map the Google Maps Map object to cluster the markers on
   * @param markers an array of Google Maps Markers to run the clustering visualization on
   */
  clusterMarkers(map: google.maps.Map, markers: Array<google.maps.Marker>) {
    return new MarkerClusterer(map, markers, {
      imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
    });
  }

  /**
   * Given two GPS coordinates, returns the bearing in degrees if the pathtaker had moved
   * directly from previous point to current point
   *
   * Should probably just use the Google Maps SDK Geometry Library, but this works too
   *
   * @param current the current location as IGeoCoords
   * @param previous the previous location as IGeoCoords
   */
  calculateBearing(current: IGeoCoords, previous: IGeoCoords): number {
    /**
     * Converts a provided angle in degrees to radians
     *
     * @param degrees an angle in degrees to be converted to radians
     *
     * TODO: clean up function naming per eslint rules.
     */
    const radians = (degrees: number) => degrees * (Math.PI / 180);

    /**
     * Converts a provided angle in radians to degrees
     *
     * @param radians an angle in radians to be converted to degrees
     *
     * TODO: clean up function name per eslint rules.
     */
    const degrees = (radians: number) => radians * (180 / Math.PI);

    const previousLat = radians(previous.lat);
    const previousLong = radians(previous.lng);
    const currentLat = radians(current.lat);
    const currentLong = radians(current.lng);

    /**  */
    let dLong = currentLong - previousLong;

    const theta = Math.log(Math.tan(currentLat / 2.0 + Math.PI / 4.0) / Math.tan(previousLat / 2.0 + Math.PI / 4.0));

    if (Math.abs(dLong) > Math.PI) {
      if (dLong > 0.0) {
        dLong = -(2.0 * Math.PI - dLong);
      } else {
        dLong = 2.0 * Math.PI + dLong;
      }
    }

    return (degrees(Math.atan2(dLong, theta)) + 360.0) % 360.0;
  }

  /**
   * Service Method: Given a bearing in degrees between 0 and 360, returns a car image facing that direction
   * TODO: add color as an optional param
   *
   * @param bearing a bearing in degrees where 0 < bearing < 360
   */
  buildImage(bearing: number, color?: string) {
    if (bearing + 90 > 360) {
      bearing -= 360;
    }

    return {
      path: `M178.471,107.469c-0.036,0.001-0.15,0.018-0.24,0.02
          c-0.878,0.019-3.998,0.094-5.7,0.48c-1.903,0.431-3.806,1.018-5.48,2.02c-2.505,1.5-4.617,3.641-6.56,5.82
          c-1.622,1.818-2.862,3.944-4.2,5.98c-0.538,0.819-1.232,1.584-1.52,2.52c-0.04,0.129-0.093,0.245-0.12,0.38
          c-31.257,0.088-62.513,0.31-93.76,1.02c-18.762,0.627-37.167,10.052-47.44,25.98c-4.402,5.134-7.214,11.198-8.48,17.76
          c-7.318,19.753-4.191,41.284-4.86,61.9c-0.621,16.44,2.831,32.975,10.28,47.6c8.66,13.65,21.606,25.827,38.16,28.3
          c23.191,4.821,47.076,2.084,70.58,2.88c11.854-0.026,23.706-0.018,35.56-0.02c0.02,0.085,0.055,0.158,0.08,0.24
          c0.288,0.936,0.982,1.701,1.52,2.52c1.338,2.036,2.578,4.162,4.2,5.98c1.943,2.179,4.055,4.32,6.56,5.82
          c1.674,1.003,3.577,1.589,5.48,2.02c1.702,0.385,4.822,0.461,5.7,0.48c0.395,0.081,0.82,0.011,1.1-0.2s0.368-0.419,0.44-0.9
          s-0.001-0.689-0.32-1.9s-1.082-3.32-1.9-5.24s-2.176-4.557-3.08-6.22c-0.578-1.064-1.048-1.868-1.5-2.6
          c15.421,0.007,30.838,0.007,46.26,0.02c0.054,0.071,0.111,0.148,0.18,0.22c0.356,0.375,0.873,0.872,1.86,1.22
          c0.987,0.348,1.037,0.25,4.04,0.4c3.003,0.15,10.612,0.097,12.84-0.04s2.625-0.172,3.72-0.6c0.884-0.346,1.544-0.512,1.68-1.18
          c23.496,0.006,47.005-0.021,70.5-0.12c0.864,0.293,1.059,0.217,3.92,0.36c3.003,0.15,10.632,0.097,12.86-0.04
          c1.837-0.113,2.436-0.167,3.2-0.42c19.412-0.121,38.829-0.305,58.24-0.58c13.574,0.032,27.064-8.429,31.62-21.48
          c11.638-28.71,11.428-60.541,10.24-91.04c-1.52-19.914-4.089-40.865-14.88-58.06c-9.668-13.695-28.301-14.1-43.48-14.04
          c-14.294-0.15-28.584-0.208-42.88-0.24c-0.486-0.064-1.078-0.12-2.06-0.18c-2.228-0.137-9.857-0.19-12.86-0.04
          c-1.971,0.098-2.633,0.098-3.16,0.18c-18.306-0.005-36.613,0.039-54.92,0.04c-5.439,0.029-10.881,0.042-16.32,0.06
          c-0.114-0.706-0.796-0.867-1.7-1.22c-1.095-0.428-1.492-0.463-3.72-0.6s-9.837-0.19-12.84-0.04c-3.003,0.15-3.053,0.052-4.04,0.4
          s-1.504,0.845-1.86,1.22c-0.089,0.094-0.177,0.187-0.24,0.28c-15.421,0.02-30.839,0.039-46.26,0.06
          c0.471-0.758,0.955-1.587,1.56-2.7c0.904-1.664,2.262-4.299,3.08-6.22c0.818-1.92,1.581-4.029,1.9-5.24s0.392-1.419,0.32-1.9
          c-0.072-0.481-0.16-0.689-0.44-0.9c-0.191-0.144-0.452-0.21-0.72-0.22C178.565,107.467,178.517,107.467,178.471,107.469
          L178.471,107.469z M231.051,124.549c0.569-0.006,1.216-0.006,1.84,0.02c-0.887,0.002-1.773-0.002-2.66,0
          C230.513,124.558,230.803,124.552,231.051,124.549z M392.671,131.869c3.984-0.1,8.475,0.497,10.9,1.74
          c5.937,3.043,9.497,7.731,10.88,14.34c0.451,2.157,1.527,5.292,2.38,6.98c2.538,5.022,4.874,12.282,4.66,14.46l-0.2,2.02
          l-1.98-1.86c-1.088-1.02-2.95-3.953-4.12-6.52c-6.772-14.861-15.816-23.517-29.6-28.36c-1.199-0.421-1.19-0.468-0.06-1.3
          C386.789,132.443,389.572,131.947,392.671,131.869L392.671,131.869z M178.331,133.129c1.289-0.008,2.762-0.003,4.4,0
          c10.918,0.021,29.102,0.262,48.16,0.66l13.6,0.28l5.3,8.32c2.911,4.567,5.377,8.74,5.48,9.28c0.185,0.969,0.136,0.967-4.1,0.9
          c-2.361-0.037-8.515-0.213-13.66-0.4c-14.533-0.528-36.943-2.857-51.1-5.3l-5.66-0.96l-1.82-2.84
          c-1.002-1.555-2.866-4.275-4.14-6.06s-2.387-3.433-2.48-3.66C172.264,133.235,174.465,133.153,178.331,133.129L178.331,133.129z
           M50.751,133.589c1.039,0,2.219-0.006,3.56,0c14.446,0.06,17.025,1.169,11.5,4.9c-3.192,2.156-23.743,14.638-32.06,19.48
          c-7.072,4.117-9.857,6.488-13.02,11.08c-1.273,1.848-3.207,3.772-4.32,4.3c-2.067,0.981-5.292,1.268-6.02,0.54
          c-0.226-0.226-0.42-2.035-0.42-4.02c0-3.856,1.171-6.737,5.4-13.4c3.48-5.483,10.249-13.165,14.26-16.18
          c2.052-1.542,6.01-3.7,8.8-4.78C42.838,133.803,43.475,133.589,50.751,133.589L50.751,133.589z M268.251,134.409
          c1.649-0.004,4.244,0.059,8.26,0.16c33.707,0.848,55.631,1.593,55.9,1.88c0.451,0.48,1.7,8.213,1.7,10.54
          c0,3.626,0.283,3.523-12.22,4.18c-8.366,0.44-40.073,1.428-47.62,1.48c-0.734,0.005-1.541-1.452-4.66-8.4
          c-2.074-4.619-3.916-8.752-4.08-9.18C265.354,134.606,265.503,134.415,268.251,134.409L268.251,134.409z M352.591,139.249
          c0.728-0.024,1.629,0.029,2.78,0.12c4.51,0.359,15.212,1.876,15.8,2.24c1.263,0.781-5.239,3.088-13.2,4.7
          c-5.925,1.2-6.614,1.27-8.08,0.68c-1.362-0.548-1.632-2.195-0.78-4.76C349.835,140.049,350.408,139.32,352.591,139.249z
           M113.871,142.989c1.925,0,55.013,11.673,64.22,14.12c6.208,1.65,17.614,5.504,25.34,8.56c17.578,6.952,26.57,9.32,35.44,9.32
          c6.752,0,7.142,0.179,12.68,6.1l5.7,6.1v30.24v30.26l-5.56,5.94l-5.54,5.92l-10.76,0.64c-8.487,0.496-13.218,1.589-22.44,5.18
          c-22.98,8.949-37.944,13.313-65.84,19.26c-15.566,3.318-29.535,6.317-31.04,6.66c-2.204,0.502-3.188-0.271-5.08-4
          c-2.909-5.735-9.306-25.696-11.14-34.78c-4.045-20.033-3.727-55.656,0.64-73.22C104.563,162.909,111.907,142.989,113.871,142.989
          L113.871,142.989z M399.071,149.289c3.395-0.166,4.423,2.766,8.12,9.7c10.055,18.859,13.099,35.101,12.16,65
          c-0.737,23.458-2.732,32.473-10.86,49c-7.083,14.402-7.268,14.5-17.72,9.52c-4.738-2.257-11.808-5.301-15.7-6.76
          s-7.08-2.875-7.08-3.14s1.93-4.293,4.28-8.96c7.376-14.648,9.794-26.292,9.72-46.82c-0.075-20.549-2.245-30.756-9.68-45.52
          c-2.46-4.884-4.293-9.025-4.08-9.2s7.532-3.43,16.26-7.24C392.371,151.429,396.431,149.418,399.071,149.289L399.071,149.289z
           M11.851,260.189c0.488-0.043,1.092,0.041,1.84,0.22c3.659,0.874,4.193,1.296,7.28,5.54c3.662,5.035,5.749,6.701,14.84,11.96
          c13.472,7.793,31.589,19.145,32.24,20.2c1.395,2.262-0.265,2.64-12.72,2.78c-10.284,0.115-12.234-0.022-15.04-1.04
          c-9.38-3.403-16.845-9.753-23.88-20.32c-5.178-7.777-6.403-10.62-6.42-14.86C9.979,261.562,10.386,260.317,11.851,260.189
          L11.851,260.189z M421.291,263.409l0.18,1.84c0.217,2.132-2.37,10.221-4.56,14.22c-0.818,1.493-1.902,4.672-2.4,7.06
          c-2.12,10.167-9.973,16.474-20.26,16.3c-2.053-0.035-4.573-0.254-5.6-0.5s-2.402-0.752-3.06-1.12
          c-1.119-0.626-1.075-0.708,0.64-1.4c7.987-3.225,12.381-5.899,16.6-10.06c5.036-4.966,8.203-9.575,12.3-17.94
          c1.556-3.178,3.565-6.377,4.48-7.1L421.291,263.409z M273.371,282.069l8.72,0.1c10.585,0.12,31.399,0.849,42.66,1.48
          c6.71,0.376,8.359,0.591,8.8,1.16c0.75,0.967,0.671,4.788-0.22,9.44l-0.76,3.9l-1.96,0.3c-1.899,0.287-24.574,0.961-51.92,1.54
          c-9.474,0.201-13.399,0.148-13.28-0.18c0.093-0.256,1.92-4.352,4.06-9.1L273.371,282.069L273.371,282.069z M247.611,282.249
          c4.695-0.008,7.74,0.179,7.74,0.62c0,0.57-9.584,16.057-10.62,17.16c-0.511,0.543-11.651,0.836-60.18,1.56l-12.7,0.2l1.78-2.46
          c0.985-1.351,2.976-4.281,4.42-6.5c1.602-2.461,2.989-4.117,3.54-4.24c4.928-1.102,23.755-3.611,35.48-4.74
          C227.408,282.854,239.786,282.262,247.611,282.249L247.611,282.249z M352.191,287.529c1.809,0.025,4.377,0.466,7.88,1.26
          c8.774,1.988,13.206,3.948,10.28,4.56c-2.82,0.59-11.111,1.606-15.22,1.86l-4.42,0.26l-0.88-1.44
          c-0.482-0.793-1.002-2.208-1.16-3.14C348.277,288.555,349.176,287.487,352.191,287.529L352.191,287.529z`,
      fillColor: color || '#4064BD',
      fillOpacity: 1,
      strokeColor: 'black',
      strokeOpacity: 0.6,
      strokeWeight: 1,
      scale: 0.13,
      rotation: bearing + 90,
      anchor: new google.maps.Point(111, 220)
    };
  }

  /**
   * Gets user location using the Geolocation API
   *
   * @returns Observable<IGeoCoords>
   */
  getUserLocation(): Observable<IGeoCoords> {
    const location = new Subject<IGeoCoords>();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        location.next({ lat: position.coords.latitude, lng: position.coords.longitude });
        location.complete();
      },
      err => location.error(new ServiceException("Could not get user location")),
      { enableHighAccuracy: false, maximumAge: Infinity })
    } else {
      location.next(null);
      location.complete();
    }
    return location;
  }

  /**
   * Converts a GoogleMaps array of autocomplete results to a more general AutoCompleteResult array.
   *
   * @param suggestions
   *
   * TODO: replace object with more appropriate type.
   */
  private convertGoogleMapsAutoSuggestResultsToAutoCompleteResults(suggestions: Array<any>): Array<AutoCompleteResult> {
    const autoCompleteResults: Array<AutoCompleteResult> = [];

    if (!isNullOrUndefined(suggestions)) {
      suggestions.map(suggestion => {
        const autoCompleteResult: AutoCompleteResult = new AutoCompleteResult();
        autoCompleteResult.label = suggestion['description'];
        autoCompleteResult.locationId = suggestion['place_id'];
        if (!isNullOrUndefined(suggestion['structured_formatting'])) {
          autoCompleteResult.mainText = suggestion['structured_formatting']['main_text'];
          autoCompleteResult.secondaryText = suggestion['structured_formatting']['secondary_text'];
        }
        autoCompleteResults.push(autoCompleteResult);
      });
    }

    return autoCompleteResults;
  }

  /**
   * Converts a Google Maps reverse geocode result to a Uzurv Location result.
   *
   * @param hereMapsResult
   */
  private convertGoogleMapsGeocodeResultToUzurvLocation(
    geocodeResult: google.maps.GeocoderResult,
    autoCompleteResult?: AutoCompleteResult
  ): Location {
    const location = new Location();

    let streetNumber = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'street_number', 'long_name');
    const route = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'route', 'long_name');
    const streetAddress2 = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'room', 'long_name');
    const locality = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'locality', 'long_name');
    // TODO: These are initialized but not used. Can we can them?
    // const neighborhood = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'neighborhood', 'long_name');
    // const county = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'administrative_area_level_2', 'long_name');
    const stateCode = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'administrative_area_level_1', 'short_name');
    const zipCode = this.extractAddressComponentFromGoogleGeocodeResult(geocodeResult, 'postal_code', 'long_name');

    // Use autocomplete result to fill in missing address components
    if (!isNullOrUndefined(autoCompleteResult)) {
      const autoCompleteResultComponents: Array<string> = autoCompleteResult.label.split(' ');

      if (isNullOrUndefined(streetNumber)) {
        if (!isNaN(+autoCompleteResultComponents[0])) {
          streetNumber = autoCompleteResultComponents[0];
        }
      }
    }

    location.streetAddress1 = `${streetNumber} ${route}`;
    location.streetAddress2 = streetAddress2;
    location.city = locality;
    location.stateCode = stateCode;
    location.zipCode = zipCode;
    location.display = geocodeResult.formatted_address;
    location.latitude = geocodeResult.geometry.location.lat();
    location.longitude = geocodeResult.geometry.location.lng();

    return location;
  }

  /**
   * Returns an address component by property name if it exists in the Geocode results array
   *
   * @param geocodeResult
   * @param propertyName how the component is categorized by Google
   * @param nameType either 'long_name' or 'short_name
   */
  private extractAddressComponentFromGoogleGeocodeResult(
    geocodeResult: google.maps.GeocoderResult,
    propertyName: string,
    nameType
  ): string {
    try {
      return geocodeResult.address_components[
        geocodeResult.address_components.findIndex(component => component.types.includes(propertyName))
      ][nameType];
    } catch (err) {
      return null;
    }
  }
}
